import React from 'react';

// Brand Text Component - kept for backwards compatibility if used elsewhere
export const NECSBrandText = ({
  size = 'md',
  showTagline = true,
  align = 'center',
  className = '',
  dataTestId
}) => {
  const sizes = {
    sm: {
      noble: 'text-base sm:text-lg',
      cleaning: 'text-[9px] sm:text-[10px]',
      tagline: 'text-[8px] sm:text-[9px]',
      crown: 'w-3 h-3',
      gap: 'gap-1'
    },
    md: {
      noble: 'text-xl sm:text-2xl',
      cleaning: 'text-xs sm:text-sm',
      tagline: 'text-[10px] sm:text-[11px]',
      crown: 'w-4 h-4',
      gap: 'gap-1'
    },
    lg: {
      noble: 'text-3xl sm:text-4xl',
      cleaning: 'text-base sm:text-lg',
      tagline: 'text-xs sm:text-sm',
      crown: 'w-5 h-5',
      gap: 'gap-1.5'
    },
    xl: {
      noble: 'text-4xl sm:text-5xl lg:text-6xl',
      cleaning: 'text-lg sm:text-xl',
      tagline: 'text-sm',
      crown: 'w-6 h-6',
      gap: 'gap-2'
    }
  };

  const s = sizes[size] || sizes.md;
  const alignment = align === 'left' ? 'items-start text-left' : 'items-center text-center';
  const crownAlignment = align === 'left' ? 'self-start' : 'self-center';

  const CrownIcon = ({ className, style }) => (
    <svg viewBox="0 0 512 512" fill="currentColor" className={className} style={style}>
      <path d="M496 128c-26.5 0-48 21.5-48 48 0 11.2 4.1 21.3 10.7 29.3l-81.5 106-74.8-174.6c17.5-6.7 30-23.7 30-43.5 0-26.5-21.5-48-48-48s-48 21.5-48 48c0 19.8 12.5 36.8 30 43.5L191.6 311.3l-81.5-106C116.7 197.3 120.8 187.2 120.8 176c0-26.5-21.5-48-48-48s-48 21.5-48 48c0 23.3 16.7 42.6 38.6 47.3L112 400h288l48.6-176.7c21.9-4.7 38.6-24 38.6-47.3 0-26.5-21.5-48-48-48zM128 432h256v32H128z" />
    </svg>
  );

  return (
    <div className={`flex flex-col ${alignment} ${className}`} data-testid={dataTestId}>
      <div className={`flex items-center justify-center ${crownAlignment} mb-1`}>
        <CrownIcon className={s.crown} style={{ color: '#FFD700', filter: 'drop-shadow(0px 2px 2px rgba(0,0,0,0.5))' }} />
      </div>

      <span
        className={`${s.noble} font-bold whitespace-nowrap`}
        style={{
          color: '#FFD700',
          fontFamily: '"Cinzel", "Times New Roman", serif',
          letterSpacing: '0.15em',
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
        }}
      >
        NOBLE EMPIRE
      </span>

      <span
        className={`${s.cleaning} font-medium whitespace-nowrap`}
        style={{
          color: '#FFFFFF',
          fontFamily: '"Montserrat", "Inter", sans-serif',
          letterSpacing: '0.3em',
          textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
          marginTop: '2px'
        }}
      >
        CLEANING SERVICES, LLC
      </span>

      {showTagline && (
        <span
          className={`${s.tagline} italic whitespace-nowrap`}
          style={{
            color: '#FFD700',
            fontFamily: '"Montserrat", "Inter", sans-serif',
            letterSpacing: '0.2em',
            marginTop: '4px'
          }}
        >
          SERVICES YOU CAN TRUST
        </span>
      )}
    </div>
  );
};

// NECS Logo Component - Uses the original company logo image as requested
const NECSLogo = ({
  variant = 'default',
  size = 'md',
  className = '',
  dataTestId,
  showTagline = true,
  align = 'center'
}) => {
  const sizes = {
    sm: 'h-20 sm:h-24',
    md: 'h-24 sm:h-32',
    lg: 'h-32 sm:h-40',
    xl: 'h-40 sm:h-48'
  };

  // Header variant should render crisp & compact (no upscale)
  const isHeader = variant === 'header';
  const scaleClass = isHeader ? '' : 'transform scale-125';

  return (
    <div className={`flex items-center ${align === 'center' ? 'justify-center' : 'justify-start'} ${className}`} data-testid={dataTestId}>
      <img 
        src="https://customer-assets.emergentagent.com/job_dd9d11af-d6a7-4250-be19-4525ac61484c/artifacts/yzmg6t2c_53277.jpg"
        alt="Noble Empire Cleaning Services"
        className={`${sizes[size] || sizes.md} w-auto object-contain ${scaleClass}`}
        style={{ transformOrigin: align === 'center' ? 'center' : 'left center' }}
      />
    </div>
  );
};

// QR Code styled like business card (gold with rounded corners)
export const NECSQRCode = ({ data, size = 128 }) => {
  return (
    <div 
      className="relative rounded-2xl overflow-hidden p-3"
      style={{ 
        backgroundColor: '#3D3D3D',
        border: '3px solid #C9A962'
      }}
    >
      <img 
        src={`data:image/png;base64,${data}`}
        alt="QR Code"
        className="rounded-lg"
        style={{ 
          width: size, 
          height: size,
          filter: 'sepia(100%) saturate(300%) brightness(70%) hue-rotate(10deg)'
        }}
      />
      <div 
        className="absolute inset-3 rounded-lg pointer-events-none mix-blend-overlay"
        style={{ backgroundColor: 'rgba(201, 169, 98, 0.3)' }}
      />
    </div>
  );
};

export const AppTypeBadge = ({ type }) => {
  const isStaff = type === 'staff';
  
  return (
    <span 
      className={`text-xs px-2 py-1 rounded-full font-medium ${
        isStaff 
          ? 'bg-[#C9A962] text-[#2D2D2D]' 
          : 'bg-white/20 text-white'
      }`}
    >
      {isStaff ? 'STAFF' : 'RESIDENT'}
    </span>
  );
};

export default NECSLogo;
